/**
 * Structured render-throw record.
 *
 * WHY THIS EXISTS
 * The app-shell `ChunkLoadErrorBoundary` deliberately does NOT `console.error`
 * (zero-tolerance console policy) and re-throws non-chunk errors from render(),
 * so React unmounts the whole tree and `#root` is emptied. The result was a
 * SILENT blank page: no console entry, no `pageerror`, and the only surviving
 * symptom in an E2E run was `element(s) not found` on whatever shell anchor the
 * spec happened to wait for — indistinguishable from a genuinely missing element.
 *
 * The fix is NOT to relax the console policy. It is to keep the throw as
 * STRUCTURED, machine-readable data on `window`, which:
 *   - emits nothing to the console (policy intact),
 *   - is readable from a Playwright `page.evaluate`, so the shared shell-wait
 *     helper can name the actual error in its failure message
 *     (see `frontend/tests/utils/appRootDiagnostics.ts`).
 *
 * The record is bounded and every write is defensive: diagnostics must never be
 * able to break a render path.
 *
 * WHO CAN SEE IT
 * Only local development and automated browser sessions
 * (`import.meta.env.DEV || navigator.webdriver`). The record publishes error
 * messages, truncated JS stacks, React component stacks and the full
 * `window.location.href` on a global any script on the page can read, and Lehti AI
 * URLs carry property identifiers. Production has no reader for it, so
 * production writes nothing at all — see `diagnosticsAudienceIsPresent`.
 */

/** One caught render throw, in the shape E2E reads back. */
export interface RecordedRenderThrow {
  /** Which boundary caught it, e.g. 'ChunkLoadErrorBoundary'. */
  boundary: string;
  /** Error constructor name (TypeError, ChunkLoadError, …). */
  name: string;
  message: string;
  /** First frames of the JS stack, truncated. */
  stack: string | null;
  /** React component stack, truncated. */
  componentStack: string | null;
  /** Epoch ms when the throw was caught. */
  at: number;
  /** URL at the time of the throw — which route blanked. */
  href: string;
}

/** Global key the E2E diagnostics helper reads. Keep in sync with tests/utils/appRootDiagnostics.ts. */
export const RENDER_THROW_RECORD_KEY = '__FM_RENDER_THROWS__';

/** Bound on retained records — this is a diagnostic buffer, not a log sink. */
const MAX_RECORDS = 10;

/** Bound on retained text so a pathological stack cannot balloon memory. */
const MAX_TEXT_LENGTH = 4000;

declare global {
  interface Window {
    __FM_RENDER_THROWS__?: RecordedRenderThrow[];
  }
}

/**
 * Identity of one logical render throw: boundary + error class + message +
 * route.
 *
 * VALUE identity, not object identity, and that distinction is load-bearing.
 * The record is written from the render-phase `getDerivedStateFromError`, and
 * React replays the failing render, so the SAME logical throw arrives twice —
 * with a DIFFERENT `Error` instance each time, because real code does
 * `throw new Error(...)` inside the failing builder rather than re-throwing one
 * pre-built object. An object-identity dedupe (a `WeakMap` keyed on the error)
 * collapses the pair only when the identical instance is thrown twice, which is
 * a shape only a test fixture produces. Keying on the value collapses the real
 * pair, and makes the `componentDidCatch` enrichment attach to a DETERMINED
 * record instead of whichever duplicate happened to match on message alone.
 */
function recordIdentity(boundary: string, name: string, message: string, href: string): string {
  return `${boundary}|${name}|${message}|${href}`;
}

function truncate(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH)}…[truncated]` : value;
}

/**
 * True only where the record has a reader: local development and an automated
 * browser session.
 *
 * Real users get NOTHING. The record carries error messages, JS stacks, React
 * component stacks and the full `window.location.href` — and Lehti AI URLs carry
 * property identifiers — all of it readable by any script on the page. There is
 * no reader for it in production, so it is not published there.
 *
 * `navigator.webdriver` is `true` under Playwright (and every other WebDriver /
 * CDP-automated browser), which is what keeps E2E working: CI serves a
 * production build via `vite preview`, so `import.meta.env.DEV` is false there.
 */
function diagnosticsAudienceIsPresent(): boolean {
  if (import.meta.env.DEV) return true;
  return typeof navigator !== 'undefined' && navigator.webdriver === true;
}

/**
 * Record a render throw caught by an error boundary.
 *
 * Writes structured data only — never to the console. Safe to call from
 * `componentDidCatch`; it swallows its own failures so a diagnostics bug can
 * never turn into an application bug.
 */
export function recordRenderThrow(
  boundary: string,
  error: unknown,
  componentStack?: string | null,
): void {
  if (typeof window === 'undefined') return;
  if (!diagnosticsAudienceIsPresent()) return;
  try {
    const normalized = error instanceof Error ? error : new Error(String(error));
    const records = window.__FM_RENDER_THROWS__ ?? [];
    const href = typeof window.location?.href === 'string' ? window.location.href : '';
    const identity = recordIdentity(boundary, normalized.name, normalized.message, href);

    const existing = [...records]
      .reverse()
      .find((record) => recordIdentity(record.boundary, record.name, record.message, record.href) === identity);
    if (existing) {
      // Same logical throw: React's render replay, or the commit-phase hook
      // arriving after the render-phase one. Enrich with the component stack
      // only `componentDidCatch` carries; never append a duplicate.
      if (existing.componentStack === null) {
        existing.componentStack = truncate(componentStack);
      }
      return;
    }

    records.push({
      boundary,
      name: normalized.name,
      message: normalized.message,
      stack: truncate(normalized.stack),
      componentStack: truncate(componentStack),
      at: Date.now(),
      href,
    });
    if (records.length > MAX_RECORDS) {
      records.splice(0, records.length - MAX_RECORDS);
    }
    window.__FM_RENDER_THROWS__ = records;
  } catch {
    // Diagnostics must never affect rendering. If the environment refuses the
    // write (frozen window, exotic sandbox), drop the record silently — the
    // E2E helper degrades to "no recorded render throws" and still reports the
    // blank root, which is the load-bearing half of the signal.
    return;
  }
}

/** Read back the recorded throws (used by unit tests; E2E reads `window` directly). */
export function getRecordedRenderThrows(): RecordedRenderThrow[] {
  if (typeof window === 'undefined') return [];
  return window.__FM_RENDER_THROWS__ ?? [];
}

/** Clear the buffer (test hygiene only). */
export function clearRecordedRenderThrows(): void {
  if (typeof window === 'undefined') return;
  window.__FM_RENDER_THROWS__ = [];
}
